/* BitcareWeekGrid + InGridSnelplanner — Bitcare-stijl weekrooster grid.
   Extracted uit RoosterPage.jsx (feb 2026 split, deel 2).

   Per locatie een header + totalen-rij (BKR badges), dan per groep een sectie
   met kinderen-count-rij, kind-rijen met shift-blokken, medewerkers BKR-rij en
   medewerker-rijen met shift-blokken. Drag-and-drop tussen groepen ondersteund.
*/
import React from "react";
import { Copy } from "lucide-react";
import api from "@/lib/api";
import { KindDetailPopover, MedewerkerDetailPopover } from "./DetailPopovers";
import { BKRRevealPopover } from "./BKRRevealPopover";


export function BitcareWeekGrid({ data, onCellClick, onEmptyCellClick, onCopyWeek, onKindOverride, onSuggestieAdd, onGroupFocus }) {
  const days = data.days || [];
  const [showWeekend, setShowWeekend] = React.useState(false);
  // Filter dagen op basis van weekend-toggle. Za=weekday_kort "za", zo="zo".
  const visibleDays = showWeekend ? days : days.filter(d => d.weekday_kort !== "za" && d.weekday_kort !== "zo");
  const dayISOs = visibleDays.map(d => d.datum);
  const [dragKind, setDragKind] = React.useState(null); // {kind_id, from_group_id, datum, kind_naam, kind_geboortedatum}
  const [dropTarget, setDropTarget] = React.useState(null); // {group_id, datum}
  const [copyDialogOpen, setCopyDialogOpen] = React.useState(false);
  const [copyTargetWeek, setCopyTargetWeek] = React.useState("");

  // WEBSTER KOV — Bij openen copy-dialoog: default doelweek = huidige week + 7 dagen
  React.useEffect(() => {
    if (copyDialogOpen && !copyTargetWeek && data.week_start) {
      const nxt = new Date(data.week_start + "T00:00:00");
      nxt.setDate(nxt.getDate() + 7);
      setCopyTargetWeek(nxt.toISOString().slice(0, 10));
    }
  }, [copyDialogOpen, copyTargetWeek, data.week_start]);

  // WEBSTER KOV — BKR live-preview tijdens drag: bereken wat er gebeurt in de
  // doelgroep als het gesleepte kind erbij komt. We houden de _huidige_ BKR van
  // de doelgroep aan (uit medewerkers_per_dag) en tellen simpelweg 1 kind extra
  // in de leeftijd-bin. Deze schatting is voldoende voor "past het?"-oordeel.
  const previewBKR = React.useCallback((toGrp, iso) => {
    if (!toGrp || !iso) return null;
    const dagRow = (toGrp.medewerkers_per_dag || []).find(m => m.datum === iso);
    if (!dagRow) return null;
    const kidRow = (toGrp.kinderen_per_dag || []).find(k => k.datum === iso);
    const nu_kinderen = (kidRow?.count || 0) + 1;
    // Zonder leeftijd-mix info gebruiken we een conservatieve ratio: 1 pm / 5 kinderen
    // Dit is een INDICATOR, backend blijft SSOT
    const geschatte_vereist = Math.max(dagRow.vereist, Math.ceil(nu_kinderen / 5));
    const nieuwe_status = dagRow.medewerkers >= geschatte_vereist ? "ok"
                      : (geschatte_vereist - dagRow.medewerkers) === 1 ? "warning"
                      : "alert";
    return {
      datum: iso, kinderen: nu_kinderen,
      medewerkers: dagRow.medewerkers, vereist: geschatte_vereist,
      status: nieuwe_status,
    };
  }, []);

  const badgeColor = (status) =>
    status === "ok" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : status === "warning" ? "bg-amber-100 text-amber-800 border-amber-200"
    : "bg-red-100 text-red-800 border-red-200";

  const isWeekend = (iso) => {
    const dt = new Date(iso + "T00:00:00");
    return dt.getDay() === 0 || dt.getDay() === 6;
  };

  const findShift = (shifts_per_dag, iso) =>
    (shifts_per_dag || []).find(s => s.datum === iso);

  const computeLeeftijd = (gb) => {
    try {
      const gd = new Date(gb);
      const now = new Date();
      const totalM = (now.getFullYear() - gd.getFullYear()) * 12 + (now.getMonth() - gd.getMonth());
      return `${Math.floor(totalM / 12)}j ${totalM % 12}m`;
    } catch { return ""; }
  };

  return (
    <div className="border border-neutral-200 rounded-md overflow-x-auto bg-white -mx-4 sm:mx-0" data-testid="bitcare-week-grid"
         style={{ WebkitOverflowScrolling: "touch" }}>
      {onCopyWeek && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-neutral-200 bg-neutral-50 flex-wrap">
          <div className="text-[10px] sm:text-[11px] text-neutral-600 flex-1 min-w-[200px]">
            <span className="hidden sm:inline">💡 Tip: sleep een kind naar een andere groep-header voor tijdelijke herplaatsing · klik op lege cel bij medewerker om shift toe te voegen</span>
            <span className="sm:hidden">💡 Sleep zijwaarts om meer dagen te zien</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowWeekend(v => !v)}
              data-testid="grid-toggle-weekend"
              className={`text-[11px] font-medium px-2.5 py-1 rounded border ${showWeekend ? "bg-[color:var(--sage,#0d5e4a)] text-white border-[color:var(--sage,#0d5e4a)]" : "bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-100"}`}
              title={showWeekend ? "Verberg za/zo (standaard voor KDV)" : "Toon za/zo (voor uitzonderlijke openingen)"}
            >
              {showWeekend ? "🗓 za/zo aan" : "🗓 za/zo uit"}
            </button>
            <button
              onClick={() => setCopyDialogOpen(true)}
              data-testid="grid-copy-week"
              className="text-[11px] font-medium px-2.5 py-1 rounded bg-white border border-neutral-300 hover:bg-neutral-100 text-neutral-700"
              title="Kopieer huidige week rooster naar een doelweek naar keuze"
            >
              📋 Week kopiëren →
            </button>
          </div>
        </div>
      )}
      <table className="w-full text-xs border-collapse min-w-[720px] sm:min-w-[900px]" style={{ tableLayout: "fixed" }}>
        <colgroup>
          <col className="w-[130px] sm:w-[220px]" />
          {dayISOs.map(iso => <col key={iso} style={{ minWidth: "88px" }} />)}
        </colgroup>
        <thead>
          <tr className="border-b border-neutral-300 bg-neutral-50">
            <th className="text-left py-2 px-3 sticky left-0 bg-neutral-50 font-semibold text-neutral-600 uppercase text-[10px] tracking-widest z-10"></th>
            {visibleDays.map(day => (
              <th key={day.datum}
                  className={`text-left py-2 px-2 font-semibold uppercase text-[10px] tracking-widest ${isWeekend(day.datum) ? "text-neutral-400" : "text-neutral-700"}`}>
                {day.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.locations.map(loc => (
            <React.Fragment key={loc.id}>
              <tr className="bg-[color:var(--sage,#0d5e4a)]/10">
                <td colSpan={dayISOs.length + 1}
                    className="py-2 px-3 text-sm font-bold text-neutral-900 sticky left-0"
                    data-testid={`grid-loc-${loc.id}`}>
                  Locatie: {loc.name} <span className="text-[10px] font-normal text-neutral-500">({loc.type})</span>
                </td>
              </tr>
              <tr className="bg-[color:var(--sage,#0d5e4a)]/5 border-b border-neutral-200">
                <td className="py-1.5 px-3 text-[11px] font-semibold text-neutral-700 sticky left-0 bg-[color:var(--sage,#0d5e4a)]/5">
                  Totalen: {loc.name}
                </td>
                {loc.totalen_per_dag.filter(t => dayISOs.includes(t.datum)).map(t => (
                  <td key={t.datum} className="py-1.5 px-1">
                    {t.vereist === 0 ? (
                      <span className="text-neutral-300 text-[10px]">—</span>
                    ) : (
                      <BKRRevealPopover bkr={t} onGroupSelect={onGroupFocus} testid={`grid-loc-badge-${loc.id}-${t.datum}`}>
                        <button
                          type="button"
                          className={`inline-block px-1.5 py-0.5 rounded border text-[10px] font-bold tabular-nums hover:brightness-95 cursor-pointer ${badgeColor(t.status)}`}
                          data-testid={`grid-loc-badge-${loc.id}-${t.datum}`}
                          title="Klik voor locatie-BKR met per-groep breakdown"
                        >
                          {t.medewerkers}/{t.vereist}
                        </button>
                      </BKRRevealPopover>
                    )}
                  </td>
                ))}
              </tr>

              {loc.groups.map(grp => (
                <React.Fragment key={grp.id}>
                  <tr className={`border-t border-neutral-200 ${dropTarget?.group_id === grp.id ? "bg-blue-200/60" : "bg-neutral-100"}`}
                      onDragOver={(e) => {
                        if (dragKind && dragKind.from_group_id !== grp.id) {
                          e.preventDefault();
                          setDropTarget({ group_id: grp.id });
                        }
                      }}
                      onDragLeave={() => setDropTarget(null)}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (dragKind && onKindOverride && dragKind.from_group_id !== grp.id) {
                          // WEBSTER KOV — Allergie-check bij drop. Als het gesleepte
                          // kind allergieën heeft die NIET aanwezig zijn in de doel-
                          // groep (proxy: geen ander kind daar met dezelfde allergie),
                          // dan circuleert dat allergeen mogelijk vrij in die groep.
                          // Vraag bevestiging voor die risico-verplaatsing.
                          const dragAll = (dragKind.allergieen || []).map(a => a.toLowerCase());
                          if (dragAll.length > 0) {
                            // WEBSTER KOV — Priority: expliciete groep-config
                            // (`allergenen_aanwezig`). Fallback: inferred uit kids.
                            const explicit = (grp.allergenen_aanwezig || []).map(a => a.toLowerCase());
                            let risky;
                            if (explicit.length > 0) {
                              // "risky" = allergenen die aanwezig ZIJN in de groep
                              risky = dragAll.filter(a => explicit.includes(a));
                            } else {
                              const destAll = new Set();
                              (grp.kinderen || []).forEach(k => (k.allergieen || []).forEach(a => destAll.add(a.toLowerCase())));
                              risky = dragAll.filter(a => !destAll.has(a));
                            }
                            if (risky.length > 0) {
                              const confirmMsg = `⚠ ALLERGIE-WAARSCHUWING\n\n${dragKind.kind_naam} heeft allergie voor: ${risky.join(", ")}\n\nDe groep "${grp.name}" heeft ${explicit.length > 0 ? "dit allergeen geregistreerd als aanwezig" : "geen andere kinderen met deze allergie — allergeen circuleert daar mogelijk vrij"}.\n\nToch verplaatsen?`;
                              if (!window.confirm(confirmMsg)) {
                                setDragKind(null); setDropTarget(null);
                                return;
                              }
                            }
                          }
                          onKindOverride({
                            kind_id: dragKind.kind_id,
                            kind_naam: dragKind.kind_naam,
                            from_group_id: dragKind.from_group_id,
                            new_group_id: grp.id,
                            new_group_naam: grp.name,
                            datum: dragKind.datum,
                            datums: dragKind.datums || [dragKind.datum],
                          });
                        }
                        setDragKind(null); setDropTarget(null);
                      }}
                  >
                    <td colSpan={dayISOs.length + 1}
                        className="py-1.5 px-3 text-[11px] font-semibold text-neutral-800 sticky left-0"
                        data-testid={`grid-grp-${grp.id}`}>
                      Groep: {grp.name}
                      {dropTarget?.group_id === grp.id && dragKind && (() => {
                        // WEBSTER KOV — Sleep-slaan waarschuwing: toon geschatte BKR
                        // in de doelgroep als het kind erin wordt gedropt.
                        const preview = previewBKR(grp, dragKind.datum);
                        // Allergie-check: allergenen van drag-kid die NIET bij andere
                        // kinderen in doelgroep voorkomen (mogelijk circulerend).
                        const dragAll = (dragKind.allergieen || []).map(a => a.toLowerCase());
                        const explicit = (grp.allergenen_aanwezig || []).map(a => a.toLowerCase());
                        let risky;
                        if (explicit.length > 0) {
                          risky = dragAll.filter(a => explicit.includes(a));
                        } else {
                          const destAll = new Set();
                          (grp.kinderen || []).forEach(k => (k.allergieen || []).forEach(a => destAll.add(a.toLowerCase())));
                          risky = dragAll.filter(a => !destAll.has(a));
                        }
                        if (!preview) return null;
                        const tone = preview.status === "ok" ? "text-emerald-700 bg-emerald-100"
                                    : preview.status === "warning" ? "text-amber-800 bg-amber-100"
                                    : "text-red-800 bg-red-100";
                        return (
                          <>
                            <span className={`ml-3 px-2 py-0.5 rounded text-[10px] font-bold ${tone}`}
                                  data-testid={`grid-drag-bkr-preview-${grp.id}`}>
                              → BKR wordt {preview.medewerkers}/{preview.vereist} PM · {preview.kinderen} k
                              {preview.status === "ok" && " ✓ past"}
                              {preview.status === "warning" && " ⚠ krap"}
                              {preview.status === "alert" && ` ✗ ${preview.vereist - preview.medewerkers} PM tekort`}
                            </span>
                            {risky.length > 0 && (
                              <span className="ml-2 px-2 py-0.5 rounded text-[10px] font-bold bg-red-600 text-white animate-pulse"
                                    data-testid={`grid-drag-allergie-preview-${grp.id}`}>
                                ⚠ ALLERGIE: {risky.join(", ")} circuleert
                              </span>
                            )}
                          </>
                        );
                      })()}
                      {dropTarget?.group_id === grp.id && !dragKind && (
                        <span className="ml-2 text-[10px] text-blue-700 font-normal">↓ los hier om te verplaatsen</span>
                      )}
                    </td>
                  </tr>
                  {/* Medewerkers-teller (BKR-badges per dag) BOVEN kinderen — user-request */}
                  <tr className="bg-emerald-50/40 border-t border-neutral-200 border-b border-neutral-100">
                    <td className="py-1 px-3 text-[10px] font-semibold text-neutral-600 uppercase tracking-widest sticky left-0 bg-emerald-50/40">
                      Medewerkers
                    </td>
                    {grp.medewerkers_per_dag.filter(m => dayISOs.includes(m.datum)).map(m => (
                      <td key={m.datum} className="py-1 px-1">
                        {m.vereist === 0 ? (
                          <span className="text-neutral-300 text-[10px]">—</span>
                        ) : (
                          <BKRRevealPopover bkr={m} testid={`grid-grp-badge-${grp.id}-${m.datum}`}>
                            <button
                              type="button"
                              className={`inline-block px-1.5 py-0.5 rounded border text-[10px] font-bold tabular-nums hover:brightness-95 cursor-pointer ${badgeColor(m.status)}`}
                              data-testid={`grid-grp-badge-${grp.id}-${m.datum}`}
                              title="Klik voor BKR-onderbouwing"
                            >
                              {m.medewerkers}/{m.vereist}
                            </button>
                          </BKRRevealPopover>
                        )}
                      </td>
                    ))}
                  </tr>
                  {grp.medewerkers.length === 0 && (
                    <tr>
                      <td colSpan={dayISOs.length + 1}
                          className="py-2 px-3 text-[10px] text-neutral-400 italic sticky left-0 bg-white">
                        nog geen medewerkers ingeroosterd
                      </td>
                    </tr>
                  )}
                  {grp.medewerkers.map(mw => (
                    <tr key={mw.id} className="border-b border-neutral-50 hover:bg-neutral-50/30"
                        data-testid={`grid-mw-${mw.id}`}>
                      <td className="py-1 px-3 sticky left-0 bg-white">
                        <div className="text-[11px] font-medium truncate flex items-center gap-1">
                          <MedewerkerDetailPopover mwId={mw.id} weekStart={data.week_start}>
                            <button className="text-left hover:underline decoration-dotted underline-offset-2 truncate"
                                    data-testid={`grid-mw-name-${mw.id}`}>
                              {mw.naam}
                            </button>
                          </MedewerkerDetailPopover>
                          {mw.heeft_ehbo && <span className="text-red-700 text-[9px]">E</span>}
                        </div>
                      </td>
                      {dayISOs.map(iso => {
                        const s = findShift(mw.shifts_per_dag, iso);
                        if (!s) {
                          // WEBSTER KOV — Klik-om-toe-te-voegen: lege cel → prefilled shift-dialoog
                          if (onEmptyCellClick) {
                            return (
                              <td key={iso} className="py-1 px-1">
                                <button
                                  onClick={() => onEmptyCellClick({
                                    medewerker_id: mw.id,
                                    medewerker_naam: mw.naam,
                                    group_id: grp.id,
                                    location_id: loc.id,
                                    datum: iso,
                                  })}
                                  className="w-full h-6 rounded border border-dashed border-neutral-200 hover:border-[color:var(--sage,#0d5e4a)] hover:bg-emerald-50 transition-colors text-[10px] text-neutral-300 hover:text-[color:var(--sage,#0d5e4a)]"
                                  data-testid={`grid-mw-empty-${mw.id}-${iso}`}
                                  title={`+ shift voor ${mw.naam} op ${iso}`}
                                >
                                  +
                                </button>
                              </td>
                            );
                          }
                          return <td key={iso} className="py-1 px-1"></td>;
                        }
                        return (
                          <td key={iso} className="py-1 px-1">
                            <button
                              onClick={() => onCellClick && onCellClick({ ...s, medewerker_id: mw.id, medewerker_naam: mw.naam })}
                              className="px-1.5 py-1 rounded text-[10px] font-medium tabular-nums bg-[#2b7373] text-white hover:bg-[#3a8888] transition-colors w-full text-left"
                              title={`${mw.naam} · ${s.tijd}`}
                              data-testid={`grid-mw-shift-${mw.id}-${iso}`}
                            >
                              {s.tijd}
                              {(s.is_opener || s.is_sluiter) && (
                                <span className="ml-1 opacity-80">
                                  {s.is_opener && "O"}{s.is_sluiter && "S"}
                                </span>
                              )}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* WEBSTER KOV — Snelplanner in Grid: toon top-3 PMs als deze groep
                      op enige dag deze week in het rood staat (BKR-alert). */}
                  {grp.medewerkers_per_dag.filter(m => dayISOs.includes(m.datum)).some(m => m.status === "alert") && onSuggestieAdd && (
                    <InGridSnelplanner
                      groupId={grp.id}
                      groupNaam={grp.name}
                      alertDay={grp.medewerkers_per_dag.find(m => m.status === "alert" && dayISOs.includes(m.datum))?.datum}
                      colspan={dayISOs.length + 1}
                      onSuggestieAdd={onSuggestieAdd}
                      locationId={loc.id}
                    />
                  )}
                  {/* Kinderen-teller + kind-rijen ONDER medewerkers — user-request */}
                  <tr className="bg-amber-50/40 border-t border-neutral-200 border-b border-neutral-100">
                    <td className="py-1 px-3 text-[10px] font-semibold text-neutral-600 uppercase tracking-widest sticky left-0 bg-amber-50/40">
                      Kinderen
                    </td>
                    {grp.kinderen_per_dag.filter(k => dayISOs.includes(k.datum)).map(k => (
                      <td key={k.datum} className="py-1 px-1">
                        {k.count > 0 ? (
                          <span className="inline-block px-1.5 py-0.5 rounded bg-amber-200/60 text-amber-900 text-[10px] font-bold tabular-nums">
                            {k.count}
                          </span>
                        ) : <span className="text-neutral-300 text-[10px]">—</span>}
                      </td>
                    ))}
                  </tr>
                  {grp.kinderen.map(kid => (
                    <tr key={kid.id} className={`border-b border-neutral-50 hover:bg-neutral-50/30 ${dragKind?.kind_id === kid.id ? "opacity-50" : ""}`}
                        data-testid={`grid-kind-${kid.id}`}>
                      <td className="py-1 px-3 sticky left-0 bg-white"
                          draggable={!!onKindOverride && !kid.is_extra}
                          onDragStart={(e) => {
                            if (kid.is_extra) return;
                            // WEBSTER KOV — Sleep meerdere dagen: verzamel ALLE
                            // shift-dagen (excl. absents) én allergieën voor
                            // veiligheids-preview bij de doelgroep.
                            const allShiftDays = (kid.shifts_per_dag || [])
                                .filter(s => !s.is_absent)
                                .map(s => s.datum);
                            const firstShiftDay = allShiftDays[0] || dayISOs[0];
                            setDragKind({
                              kind_id: kid.id, from_group_id: grp.id,
                              datum: firstShiftDay, kind_naam: kid.naam,
                              datums: allShiftDays.length ? allShiftDays : [firstShiftDay],
                              allergieen: kid.allergieen || [],
                            });
                            e.dataTransfer.effectAllowed = "move";
                          }}
                          onDragEnd={() => { setDragKind(null); setDropTarget(null); }}
                          style={{ cursor: onKindOverride && !kid.is_extra ? "grab" : "default" }}
                          title={onKindOverride && !kid.is_extra ? "Sleep naar een andere groep-header — verplaatst het kind voor de HELE week naar die groep" : ""}
                      >
                        <div className="text-[11px] font-medium truncate flex items-center gap-1">
                          {onKindOverride && !kid.is_extra && <span className="text-neutral-300 text-[9px]">⋮⋮</span>}
                          <KindDetailPopover kindId={kid.id} weekStart={data.week_start}>
                            <button className="text-left hover:underline decoration-dotted underline-offset-2 truncate"
                                    data-testid={`grid-kind-name-${kid.id}`}>
                              {kid.naam}
                            </button>
                          </KindDetailPopover>
                        </div>
                        {kid.geboortedatum && (
                          <div className="text-[9px] text-neutral-500">{computeLeeftijd(kid.geboortedatum)}</div>
                        )}
                        {kid.is_extra && (
                          <div className="text-[9px] text-blue-700 font-medium">extra opvang</div>
                        )}
                      </td>
                      {dayISOs.map(iso => {
                        const s = findShift(kid.shifts_per_dag, iso);
                        if (!s) return <td key={iso} className="py-1 px-1"></td>;
                        const bg = s.is_absent ? "bg-neutral-200 text-neutral-500 line-through"
                                    : s.is_extra ? "bg-blue-500 text-white"
                                    : "bg-[#2b7373] text-white";
                        return (
                          <td key={iso} className="py-1 px-1">
                            <div className={`px-1.5 py-1 rounded text-[10px] font-medium tabular-nums ${bg}`}
                                 title={s.is_absent ? "Afwezig" : s.tijd}>
                              {s.is_absent ? "afw." : s.tijd}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {/* WEBSTER KOV — Copy-week dialoog met doelweek-picker */}
      {copyDialogOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
             data-testid="copy-week-dialog"
             onClick={() => setCopyDialogOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}
               className="bg-white rounded-lg shadow-xl max-w-md w-full p-5">
            <div className="text-lg font-bold text-neutral-900 mb-1">Week kopiëren</div>
            <div className="text-[12px] text-neutral-600 mb-4">
              Bron-week: <strong>{data.week_start}</strong> (huidige week).
              Alle shifts op de doelweek worden vervangen.
            </div>
            <label className="block text-[11px] font-semibold text-neutral-700 uppercase tracking-widest mb-1">
              Doelweek (maandag)
            </label>
            <input type="date"
                   value={copyTargetWeek}
                   onChange={(e) => setCopyTargetWeek(e.target.value)}
                   data-testid="copy-week-target"
                   className="w-full text-sm border-2 rounded px-2 py-2 mb-3" />
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="text-[10px] text-neutral-500">Snel:</span>
              {[1, 2, 4, 6, 8, 12].map(wks => {
                const d = new Date(data.week_start + "T00:00:00");
                d.setDate(d.getDate() + 7 * wks);
                const iso = d.toISOString().slice(0, 10);
                return (
                  <button key={wks} onClick={() => setCopyTargetWeek(iso)}
                          data-testid={`copy-week-preset-${wks}`}
                          className={`text-[10px] px-2 py-0.5 rounded border ${copyTargetWeek === iso ? "bg-[color:var(--sage,#0d5e4a)] text-white border-[color:var(--sage,#0d5e4a)]" : "bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50"}`}>
                    +{wks}wk
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setCopyDialogOpen(false)}
                      className="text-[12px] px-3 py-1.5 rounded border border-neutral-300 hover:bg-neutral-50"
                      data-testid="copy-week-cancel">
                Annuleren
              </button>
              <button
                onClick={() => {
                  if (!copyTargetWeek) return;
                  onCopyWeek({ from_week_start: data.week_start, target_week_start: copyTargetWeek });
                  setCopyDialogOpen(false);
                }}
                disabled={!copyTargetWeek || copyTargetWeek === data.week_start}
                data-testid="copy-week-confirm"
                className="text-[12px] font-medium px-3 py-1.5 rounded bg-[color:var(--sage,#0d5e4a)] text-white hover:opacity-90 disabled:opacity-40"
              >
                Kopieer naar {copyTargetWeek || "…"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



/**
 * WEBSTER KOV — InGridSnelplanner
 *
 * Inline suggestion-strip die verschijnt onder een groep-medewerker-rij in
 * de BitcareWeekGrid ZODRA die groep op enige dag deze week onder BKR staat.
 * Hergebruikt bestaand /planning/suggesties endpoint met AI-ranking.
 */
export function InGridSnelplanner({ groupId, groupNaam, alertDay, colspan, onSuggestieAdd, locationId }) {
  const [rows, setRows] = React.useState(null);
  React.useEffect(() => {
    let cancel = false;
    api.get("/planning/suggesties", { params: { datum: alertDay, group_id: groupId } })
      .then(r => { if (!cancel) setRows(r.data?.suggesties || []); })
      .catch(() => { if (!cancel) setRows([]); });
    return () => { cancel = true; };
  }, [alertDay, groupId]);

  if (rows === null) return null;
  if (rows.length === 0) {
    return (
      <tr className="bg-red-50/60 border-b border-red-100">
        <td colSpan={colspan} className="py-1.5 px-3 text-[10px] text-red-800 sticky left-0 bg-red-50/60">
          ⚠ {groupNaam}: onder BKR op {alertDay} · geen gekwalificeerde PMs met open uren
        </td>
      </tr>
    );
  }
  return (
    <tr className="bg-red-50/40 border-b border-red-100">
      <td colSpan={colspan} className="py-1.5 px-3 sticky left-0 bg-red-50/40">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest font-semibold text-red-800">
            📌 {groupNaam} onder BKR op {alertDay} · suggesties:
          </span>
          {rows.slice(0, 3).map((s, idx) => (
            <button
              key={s.medewerker_id}
              onClick={() => onSuggestieAdd({
                medewerker_id: s.medewerker_id,
                group_id: groupId,
                location_id: locationId,
                datum: alertDay,
              })}
              data-testid={`grid-suggestie-${groupId}-${s.medewerker_id}`}
              className={`text-[10px] font-medium px-2 py-0.5 rounded border transition-colors flex items-center gap-1 ${idx === 0 ? "bg-[color:var(--sage,#0d5e4a)] text-white border-[color:var(--sage,#0d5e4a)] hover:opacity-90" : "bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-100"}`}
              title={`${s.open_uren}u open · ${s.match_redenen?.join(" · ") || "beschikbaar"}`}
            >
              {idx === 0 && <span className="text-[9px]">★</span>}
              + {s.naam}
              <span className="opacity-70">({s.open_uren}u)</span>
              {s.heeft_ehbo && <span className="text-[9px] opacity-80">E</span>}
            </button>
          ))}
        </div>
      </td>
    </tr>
  );
}

